import { Home, Refresh } from "@mui/icons-material";
import { Box, CircularProgress, Grid, IconButton, Tooltip } from "@mui/material";
import { COLORS } from "assets";
import { BoxSelect, FilterBar, SelectInput, SiteSelect } from "components";
import { BREAK_GLASS_APPROVED_EVENT, ENDPOINTS, filterModelsForUI, MODELS } from "config/constants";
import { useContextController } from "context";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { PrivateLayout } from "layouts";
import ThreatsTable from "pages/threats/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MILLISECONDS, ModelTypes, SiteModel, ThreatModel } from "types";
import { ensureBreakGlassForSite } from "utils";
import Bar from "./bar";
import CountChart from "./count";
import Pie from "./pie";

interface Data {
  threats?: ThreatModel[];
  totalRecords?: number;
}
const PERIODS = [
  { label: "Last 24 Hours", value: MILLISECONDS.DAY },
  { label: "Last Week", value: MILLISECONDS.WEEK },
  { label: "Last Month", value: MILLISECONDS.MONTH }
];

export default function Dashboard() {
  const t = useTranslation();
  const [{ userData }] = useContextController();
  const isSuper = userData?.type === "super";

  const [reload, setReload] = useState(false);
  const [period, setPeriod] = useState(PERIODS[0]);
  const [siteId, setSiteId] = useState<string>(() => localStorage.getItem("siteId") || "");
  const [sites, setSites] = useState<SiteModel[]>([]);
  const [boxId, setBoxId] = useState<string>();
  const { isLoading, response, callFetch } = useFetch<Data>("GET");
  const [pendingSiteId, setPendingSiteId] = useState<string | null>(null);
  const { isLoading: isLoadingOld, response: responseOld, callFetch: callFetchOld } = useFetch<Data>("GET");
  const loading = useMemo(() => isLoading || isLoadingOld, [isLoading, isLoadingOld]);
  const onReload = useCallback(() => setReload((prevState) => !prevState), []);
  const enabledModels = useMemo<ModelTypes[]>(() => {
    const models = sites.find((item) => item._id === siteId)?.enabledModels;
    return filterModelsForUI(models as ModelTypes[]);
  }, [sites, siteId]);

  const applySite = useCallback((id: string) => {
    setSiteId(id);
    localStorage.setItem("siteId", id);
    localStorage.removeItem("boxId");
    setBoxId(undefined);
    setPendingSiteId(null);
  }, []);

  useEffect(() => {
    if (!sites.length) return;
    if (siteId && sites.some((site) => site._id === siteId)) return;
    // Supers pick a site explicitly (and unlock via break-glass when needed).
    if (isSuper) return;
    const next = sites[0]._id;
    setSiteId(next);
    localStorage.setItem("siteId", next);
  }, [sites, siteId, isSuper]);

  useEffect(() => {
    const onApproved = (event: Event) => {
      const approved = (event as CustomEvent<{ siteId?: string }>).detail?.siteId;
      if (pendingSiteId && approved && String(approved) === String(pendingSiteId)) {
        applySite(pendingSiteId);
      }
      onReload();
    };
    window.addEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
    return () => window.removeEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
  }, [pendingSiteId, applySite, onReload]);

  useEffect(() => {
    if (!boxId) return;
    const old = new Date(Date.now() - period.value * 2);
    const from = new Date(Date.now() - period.value);
    const to = new Date(Date.now());
    const searchParams = new URLSearchParams();
    searchParams.append("boxId", boxId);
    searchParams.append("limit", "0");
    const params = searchParams.toString();
    const searchParamsNew = new URLSearchParams();
    searchParamsNew.append("from", from.toISOString());
    searchParamsNew.append("to", to.toISOString());
    const searchParamsOld = new URLSearchParams();
    searchParamsOld.append("from", old.toISOString());
    searchParamsOld.append("to", from.toISOString());
    callFetch(ENDPOINTS.threats + "?" + params + "&" + searchParamsNew.toString()).catch(console.error);
    callFetchOld(ENDPOINTS.threats + "?" + params + "&" + searchParamsOld.toString()).catch(console.error);
  }, [callFetch, callFetchOld, boxId, period, reload]);

  return (
    <PrivateLayout canAccess={() => true}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2.5} flexWrap="wrap">
        <Tooltip title={t["Home"]}>
          <IconButton
            component={Link}
            to="/"
            size="small"
            sx={{
              color: COLORS.surface.secondaryText,
              border: `1px solid ${COLORS.surface.border}`,
              borderRadius: `${COLORS.radius.pill}px`,
              bgcolor: COLORS.surface.card,
              width: 40,
              height: 40,
              flexShrink: 0
            }}
          >
            <Home sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <FilterBar
          light
          trailing={
            loading ? (
              <IconButton
                disabled
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  color: COLORS.surface.secondaryText,
                  border: `1px solid ${COLORS.surface.border}`,
                  bgcolor: COLORS.surface.card,
                  borderRadius: `${COLORS.radius.pill}px`
                }}
              >
                <CircularProgress size={18} color="inherit" />
              </IconButton>
            ) : (
              <Tooltip title={t["Refresh"]}>
                <IconButton
                  onClick={onReload}
                  aria-label={t["Refresh"]}
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    color: COLORS.surface.primaryText,
                    border: `1px solid ${COLORS.surface.border}`,
                    bgcolor: COLORS.surface.card,
                    borderRadius: `${COLORS.radius.pill}px`,
                    "&:hover": { bgcolor: COLORS.surface.tableHeader }
                  }}
                >
                  <Refresh sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )
          }
        >
          <Box sx={{ width: { xs: "100%", sm: 240 }, maxWidth: "100%", minWidth: 0, flexShrink: 1 }}>
            <SiteSelect
              hideLabel
              value={siteId || undefined}
              onSites={setSites}
              disableAutoSelect={isSuper}
              onChange={(id) => {
                if (!ensureBreakGlassForSite(userData?.type, id)) {
                  setPendingSiteId(id);
                  return;
                }
                applySite(id);
              }}
            />
          </Box>
          <Box sx={{ width: { xs: "100%", sm: 280 }, maxWidth: "100%", minWidth: 0, flexShrink: 1 }}>
            {siteId ? (
              <BoxSelect
                siteId={siteId}
                onChange={(id) => {
                  setBoxId(id);
                  if (id) localStorage.setItem("boxId", id);
                }}
                value={boxId}
                hideLabel
              />
            ) : null}
          </Box>
          <Box sx={{ width: { xs: "100%", sm: 170 }, maxWidth: "100%", minWidth: 0, flexShrink: 0 }}>
            <SelectInput
              data={PERIODS.map((elm) => ({ ...elm, label: t[elm.label] }))}
              value={period.value}
              onChange={(e) => {
                const value = Number.parseInt(e.target.value + "");
                const next = PERIODS.find((item) => item.value === value);
                if (next) setPeriod(next);
              }}
              fullWidth
              helperText=""
            />
          </Box>
        </FilterBar>
      </Box>

      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        mb={2}
        sx={{
          "& > *": {
            flex: {
              xs: "1 1 100%",
              sm: "1 1 calc(50% - 8px)",
              md: `1 1 calc(${100 / Math.min(enabledModels.length, 3)}% - 11px)`,
              lg: `1 1 calc(${100 / Math.min(enabledModels.length, 4)}% - 12px)`,
              xl: `1 1 calc(${100 / enabledModels.length}% - ${(16 * (enabledModels.length - 1)) / enabledModels.length}px)`
            },
            minWidth: 0,
            maxWidth: {
              xs: "100%",
              sm: "calc(50% - 8px)",
              md: `calc(${100 / Math.min(enabledModels.length, 3)}% - 11px)`,
              lg: `calc(${100 / Math.min(enabledModels.length, 4)}% - 12px)`,
              xl: `calc(${100 / enabledModels.length}% - ${(16 * (enabledModels.length - 1)) / enabledModels.length}px)`
            }
          }
        }}
      >
        {enabledModels.map((model) => (
          <Box key={model}>
            <CountChart
              loading={loading}
              period={t[`${period.label}`]}
              threats={response?.data?.threats}
              threatsOld={responseOld?.data?.threats}
              level={MODELS.indexOf(model)}
            />
          </Box>
        ))}
      </Box>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={6}>
          <Pie threats={response?.data?.threats} period={t[`${period.label}`]} models={enabledModels} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Bar threats={response?.data?.threats} period={t[`${period.label}`]} models={enabledModels} />
        </Grid>
      </Grid>

      <ThreatsTable
        boxId={boxId}
        siteId={siteId || undefined}
        title={t["Recent AI Detected Incidents"] || "Recent AI Detected Incidents"}
        hideExport
      />
    </PrivateLayout>
  );
}
